import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import maintenanceService from '../services/maintenanceService';
import authService from '../services/authService';
import { FaCalendarAlt, FaUserCog, FaTimes, FaWrench, FaSpinner } from 'react-icons/fa';
import '../styles/ScheduleMaintenance.css';

const ScheduleMaintenance = ({ asset, onClose, onScheduled }) => {
  const [formData, setFormData] = useState({
    maintenanceType: 'preventive',
    priorityLevel: 'medium',
    expectedCompletionDate: '',
    sentTo: '',
    checklist: [''],
    selectedSkill: ''
  });
  const [technicians, setTechnicians] = useState([]);
  const [allTechnicians, setAllTechnicians] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingTechs, setFetchingTechs] = useState(false);

  // Fetch all technicians on mount
  useEffect(() => {
    const fetchTechnicians = async () => {
      try {
        setFetchingTechs(true);
        const techs = await authService.getAllTechnicians();
        setAllTechnicians(techs || []);
        setTechnicians(techs || []);
      } catch (err) {
        console.error('Failed to fetch technicians:', err);
      } finally {
        setFetchingTechs(false);
      }
    };
    fetchTechnicians();
  }, []);

  // Filter technicians by selected skill
  const handleSkillFilter = async (skill) => {
    setFormData({ ...formData, selectedSkill: skill, sentTo: '' });
    if (!skill) {
      setTechnicians(allTechnicians);
      return;
    }
    try {
      setFetchingTechs(true);
      const filtered = await authService.getTechniciansBySkill(skill);
      setTechnicians(filtered || []);
    } catch (err) {
      console.error('Failed to filter technicians:', err);
      setTechnicians(allTechnicians);
    } finally {
      setFetchingTechs(false);
    }
  };

  // Collect unique skills from all technicians
  const allSkills = [...new Set(allTechnicians.flatMap(t => t.skills || []))].filter(Boolean);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleChecklistChange = (index, value) => {
    const updated = [...formData.checklist];
    updated[index] = value;
    setFormData({ ...formData, checklist: updated });
  };

  const addChecklistItem = () => {
    setFormData({ ...formData, checklist: [...formData.checklist, ''] });
  };

  const removeChecklistItem = (index) => {
    if (formData.checklist.length <= 1) return;
    const updated = formData.checklist.filter((_, i) => i !== index);
    setFormData({ ...formData, checklist: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const checklistItems = formData.checklist.filter(item => item.trim() !== '');
    if (checklistItems.length === 0) {
      toast.error('Please add at least one checklist item');
      return;
    }

    setLoading(true);
    try {
      // Find the selected technician's ObjectId
      const selectedTech = allTechnicians.find(t => t.name === formData.sentTo);

      const payload = {
        assetId: asset._id,
        maintenanceType: formData.maintenanceType,
        priorityLevel: formData.priorityLevel,
        checklist: checklistItems,
        sentTo: formData.sentTo || null,
        sentToId: selectedTech?._id || null,
        requiredSkill: formData.selectedSkill || null,
        expectedCompletionDate: formData.expectedCompletionDate || null
      };

      await maintenanceService.createMaintenance(payload);
      toast.success(`Maintenance scheduled for ${asset.assetName}`);
      if (onScheduled) onScheduled();
      onClose();
    } catch (err) {
      const msg = err.message || err.response?.data?.message || 'Failed to schedule maintenance';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sm-overlay" onClick={onClose}>
      <div className="sm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sm-header">
          <div className="sm-header-left">
            <FaCalendarAlt />
            <h3>Schedule Maintenance</h3>
          </div>
          <button className="sm-close" onClick={onClose}><FaTimes /></button>
        </div>

        {/* Asset Info */}
        <div className="sm-asset-info">
          <span className="sm-asset-name">{asset.assetName}</span>
          {asset.serialNumber && <span className="sm-asset-serial">{asset.serialNumber}</span>}
          <span className="sm-asset-lab">{asset.labLocation}</span>
          {asset.maintenanceFrequency && (
            <span className="sm-asset-freq">
              <FaWrench /> {asset.maintenanceFrequency} cycle
            </span>
          )}
        </div>

        <form onSubmit={handleSubmit} className="sm-form">
          <div className="sm-form-row">
            <div className="sm-form-group">
              <label>Maintenance Type</label>
              <select name="maintenanceType" value={formData.maintenanceType} onChange={handleChange}>
                <option value="preventive">Preventive</option>
                <option value="breakdown">Breakdown</option>
                <option value="calibration">Calibration</option>
              </select>
            </div>
            <div className="sm-form-group">
              <label>Priority</label>
              <select name="priorityLevel" value={formData.priorityLevel} onChange={handleChange}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>

          <div className="sm-form-group">
            <label>Expected Completion Date</label>
            <input
              type="date"
              name="expectedCompletionDate"
              value={formData.expectedCompletionDate}
              onChange={handleChange}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>

          {/* Technician Assignment */}
          <div className="sm-tech-section">
            <h4><FaUserCog /> Assign Technician</h4>
            
            <div className="sm-form-group">
              <label>Filter by Skill</label>
              <select value={formData.selectedSkill} onChange={(e) => handleSkillFilter(e.target.value)}>
                <option value="">All Skills</option>
                {allSkills.map(skill => (
                  <option key={skill} value={skill}>{skill}</option>
                ))}
              </select>
            </div>

            <div className="sm-form-group">
              <label>Select Technician</label>
              {fetchingTechs ? (
                <div className="sm-loading-techs"><FaSpinner className="spin" /> Loading technicians...</div>
              ) : (
                <select name="sentTo" value={formData.sentTo} onChange={handleChange}>
                  <option value="">-- Select Technician --</option>
                  {technicians.map(tech => (
                    <option key={tech._id} value={tech.name}>
                      {tech.name} {tech.skills?.length > 0 ? `(${tech.skills.join(', ')})` : ''}
                    </option>
                  ))}
                </select>
              )}
              {technicians.length === 0 && !fetchingTechs && (
                <small className="sm-no-techs">No technicians found{formData.selectedSkill ? ` with skill "${formData.selectedSkill}"` : ''}</small>
              )}
            </div>
          </div>

          {/* Checklist */}
          <div className="sm-form-group">
            <label>Maintenance Checklist</label>
            <div className="sm-checklist">
              {formData.checklist.map((item, idx) => (
                <div key={idx} className="sm-checklist-item">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => handleChecklistChange(idx, e.target.value)}
                    placeholder={`Task ${idx + 1}`}
                  />
                  {formData.checklist.length > 1 && (
                    <button type="button" className="sm-remove-item" onClick={() => removeChecklistItem(idx)}>
                      <FaTimes />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" className="sm-add-item" onClick={addChecklistItem}>
                + Add Task
              </button>
            </div>
          </div>

          <div className="sm-actions">
            <button type="button" className="sm-btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="sm-btn-submit" disabled={loading}>
              {loading ? <><FaSpinner className="spin" /> Scheduling...</> : 'Schedule Maintenance'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduleMaintenance;
